// 画像・動画アップロードのクライアント側の上限・許可 MIME 定義、事前検証、失敗時メッセージ、PUT 補助。
//
// 上限バイト数（IMAGE_MAX_BYTES / VIDEO_MAX_BYTES / EXPORT_MAX_BYTES）と画像の許可 MIME
// （ALLOWED_IMAGE_MIME）は **バックエンドの正本**（apps/api/src/handlers/upload-presigner.ts の
// IMAGE_MAX_BYTES / VIDEO_MAX_BYTES / EXPORT_MAX_BYTES / ALLOWED_IMAGE_MIME）および README の
// 「アップロード」節と一致させること。
// 動画はバックエンドが `video/*` を広く受理する（固定 allowlist は無い）。フロントは canPlayType で
// 再生可否を判定し、再生できない形式のみ弾く（下記 KNOWN_PLAYABLE_VIDEO_MIME は判定の floor）。
// これらを変更する場合はバックエンド側と README も同時に更新する（値のドリフトを起こさない）。
//
// 背景: これまでアップロード経路はサイズ超過・非対応形式・通信失敗・サーバエラーをすべて同一の汎用
// トースト「アップロードに失敗しました。もう一度お試しください。」へ潰しており、利用者にも運用にも
// 原因が伝わらなかった。本モジュールで上限・形式判定を一元管理し、原因の分かる具体的な文言を返す。
// 画像はさらに image-resize.ts で「上限超過なら自動縮小、非対応でもデコード可能なら自動変換」する。

export const IMAGE_MAX_BYTES = 20 * 1024 * 1024; // 20 MB（バックエンド IMAGE_MAX_BYTES と一致）
export const VIDEO_MAX_BYTES = 200 * 1024 * 1024; // 200 MB（バックエンド VIDEO_MAX_BYTES と一致）
// 全体像 PDF スナップショット（Connect Note 連携）の上限。バックエンド
// apps/api/src/handlers/upload-presigner.ts の EXPORT_MAX_BYTES と一致させること（値のドリフトを起こさない）。
export const EXPORT_MAX_BYTES = 16 * 1024 * 1024; // 16 MB（バックエンド EXPORT_MAX_BYTES と一致）

export const ALLOWED_IMAGE_MIME = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "image/svg+xml",
] as const;

// 動画の「常に再生可能とみなす floor」。canPlayType に依らず常に受理する（従来サポートの mp4/webm/mov
// のデグレ防止＋ MP4 ファミリ。video/quicktime や video/x-m4v に "" を返すブラウザでも弾かないため）。
// これは固定 allowlist ではない（バックエンドは video/* を受理し、再生可否はフロントの canPlayType で判定）。
export const KNOWN_PLAYABLE_VIDEO_MIME = [
  "video/mp4",
  "video/x-m4v",
  "video/quicktime",
  "video/webm",
] as const;

export type UploadKind = "image" | "video";

export type UploadValidation = { readonly ok: true } | { readonly ok: false; readonly message: string };

// トーストに表示する人向けの対応形式表記。
export const IMAGE_FORMAT_LABEL = "PNG / JPEG / WebP / GIF / SVG";
export const VIDEO_FORMAT_LABEL = "MP4 / WebM / MOV";

/** バイト数を人向けの MB 表記（小数 1 桁・四捨五入）にする。上限など固定値の表示に使う。 */
export function formatMb(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

/**
 * バイト数を人向けの MB 表記（小数 1 桁・切り上げ）にする。「上限を超えた実サイズ」の表示に使う。
 * 切り上げにより、上限を僅か（< 0.05MB）に超えた境界でも「20.1MB が上限の 20.0MB を超えています」の
 * ように実サイズと上限が同表記にならず、メッセージが自己矛盾しない。
 */
export function formatMbCeil(bytes: number): string {
  return `${(Math.ceil((bytes / 1024 / 1024) * 10) / 10).toFixed(1)}MB`;
}

/**
 * ブラウザで再生可能な動画 MIME かを判定する。動画はクライアント側で変換（トランスコード）できないため、
 * 「ブラウザで再生できる形式は全て受理／再生できない形式（AVI/MKV/WMV 等）だけ弾く」方針に使う。
 * - 既知の確実な再生形式（KNOWN_PLAYABLE_VIDEO_MIME＝MP4 ファミリ/WebM/MOV）は常に true（従来サポート
 *   分のデグレ防止。一部ブラウザが video/quicktime や video/x-m4v に "" を返してもアップロード可否を変えない）。
 * - それ以外は `<video>.canPlayType` が "" 以外（"maybe"/"probably"）なら再生可能とみなす。
 * - canPlayType を使えない環境（SSR/テスト）では判定不能として true（バックエンドは video/* を受理）。
 */
export function canBrowserPlayVideo(mimeType: string): boolean {
  if ((KNOWN_PLAYABLE_VIDEO_MIME as readonly string[]).includes(mimeType)) return true;
  if (typeof document === "undefined" || typeof document.createElement !== "function") return true;
  try {
    const el = document.createElement("video");
    if (typeof el.canPlayType !== "function") return true;
    return el.canPlayType(mimeType) !== "";
  } catch {
    return true;
  }
}

/**
 * 動画アップロードの事前検証。動画はクライアント変換が非現実的なため、ブラウザで再生可能な動画形式は
 * 全て受理し、再生不可（AVI/MKV/WMV 等）・空ファイル・上限超過・非動画 MIME のみ原因の分かる文言で弾く。
 * （画像は image-resize.ts の prepareImageForUpload が前処理する。本関数は動画専用。）
 */
export function validateVideoUpload(file: File): UploadValidation {
  if (!file.type || !file.type.startsWith("video/")) {
    return {
      ok: false,
      message: `この動画形式（${file.type || "形式不明"}）には対応していません。${VIDEO_FORMAT_LABEL} などの動画形式でお試しください。`,
    };
  }
  // 0 バイト（空ファイル）はバックエンドの contentLength: positive() で 400 になるため先に弾く。
  if (file.size <= 0) {
    return { ok: false, message: "動画ファイルが空です（0バイト）。別のファイルを選択してください。" };
  }
  if (file.size > VIDEO_MAX_BYTES) {
    return {
      ok: false,
      message: `動画サイズ（${formatMbCeil(file.size)}）が上限の ${formatMb(VIDEO_MAX_BYTES)} を超えています。動画を短く／軽量にして再度お試しください。`,
    };
  }
  if (!canBrowserPlayVideo(file.type)) {
    return {
      ok: false,
      message: `この動画形式（${file.type}）はブラウザで再生できないためアップロードできません。${VIDEO_FORMAT_LABEL} などに変換して再度お試しください。`,
    };
  }
  return { ok: true };
}

/**
 * presigned URL へファイルを PUT する。ブラウザの `fetch` は HTTP エラー status（403/4xx/5xx）でも
 * reject せず resolve するため、`response.ok` を検査し、失敗時は status を含むエラーで例外化する。
 * これをしないと S3 PUT 失敗が握り潰され、後続の完了通知の 404 に化けて真因が分からなくなる。
 * ネットワーク到達不能のときは `fetch` 自体が `TypeError` で reject し、その例外はそのまま伝播する。
 */
export async function putToPresignedUrl(
  uploadUrl: string,
  file: File,
  contentType: string
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "content-type": contentType },
  });
  if (!response.ok) {
    throw new Error(`S3 PUT failed: ${response.status}`);
  }
}

/**
 * アップロード失敗時のユーザー向けメッセージを生成する。失敗を 3 系統に区別する:
 * - サインイン失効: presign（認証付き REST）の呼び出しでアクセストークンを取得・リフレッシュ
 *   できなかったケース（AccessTokenUnavailableError。判定はバンドル境界の影響を受けない name で行う）。
 *   利用者が対処できる（再読み込み・再サインイン）ため、その旨を具体的に案内する。
 * - 通信失敗: ブラウザの `fetch` は接続不可・通信遮断（社内プロキシ/FW による直接アクセス遮断、
 *   CORS 不備、Mixed Content、ブラウザ拡張によるブロックなど）で `TypeError` を投げる。これらは
 *   いずれも「アップロード先へ到達できない」事象なので、通信環境の確認を促す文言でまとめて案内する。
 * - サーバ応答エラー: presign / S3 直 PUT / 完了通知が HTTP 4xx・5xx で失敗したケース（呼び出し側で
 *   `response.ok` を検査して例外化している）。利用者側では対処できないため「時間を置いて再試行」を案内する。
 * サイズ超過・非対応形式は事前検証／自動リサイズ側で扱うため、ここには到達しない。
 * 詳細な HTTP ステータスは呼び出し側の console 警告へ残す。
 */
export function describeUploadError(kind: UploadKind, error: unknown): string {
  const noun = kind === "image" ? "画像" : "動画";
  if (error instanceof Error && error.name === "AccessTokenUnavailableError") {
    return `サインイン情報の有効期限が切れているため${noun}をアップロードできませんでした。ページを再読み込みして、もう一度お試しください。`;
  }
  if (error instanceof TypeError) {
    return `通信に失敗したため${noun}をアップロードできませんでした。ネットワーク接続・社内プロキシ・ブラウザ拡張などの通信環境をご確認のうえ、もう一度お試しください。`;
  }
  return `サーバ側でエラーが発生したため${noun}をアップロードできませんでした。時間をおいて、もう一度お試しください。`;
}
